import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, XCircle, AlertCircle, RefreshCw, Plug, Unplug } from 'lucide-react';
import { Card } from '@/components/Card';
import { useApi } from '@/hooks/useApi';
import { post, patch, del } from '@/lib/api';

interface IntegrationSetting {
  id: string;
  product: 'psa' | 'books' | 'hub';
  enabled: boolean;
  status: 'connected' | 'disconnected' | 'error';
  last_sync_at?: string;
  sync_frequency: string;
  sync_direction: string;
  error_message?: string;
}

const PRODUCT_INFO = {
  psa: {
    name: 'The One PSA',
    description: 'Sync time entries and project assignments between People and PSA.',
    features: ['Time entry sync', 'Project assignment sync', 'Employee lookup'],
    direction: 'Bidirectional',
  },
  books: {
    name: 'The One Books',
    description: 'Export payroll data and compensation changes to Books for accounting.',
    features: ['Payroll data export', 'Compensation change notifications', 'Employee expense sync'],
    direction: 'One-way out',
  },
  hub: {
    name: 'The One Hub',
    description: 'Sync user directory and org structure with the Hub identity layer.',
    features: ['User directory sync', 'Org structure sync', 'SSO identity linkage'],
    direction: 'Bidirectional',
  },
} as const;

const FREQ_LABELS: Record<string, string> = {
  realtime: 'Real-time', hourly: 'Hourly', daily: 'Daily', manual: 'Manual',
};

const DIR_LABELS: Record<string, string> = {
  one_way_in: '← Inbound only', one_way_out: '→ Outbound only', bidirectional: '↔ Bidirectional',
};

function StatusIcon({ status }: { status: string }) {
  if (status === 'connected') return <CheckCircle2 className="w-4 h-4 text-green-400" />;
  if (status === 'error') return <AlertCircle className="w-4 h-4 text-red-400" />;
  return <XCircle className="w-4 h-4 text-muted-foreground" />;
}

export function IntegrationsAdminPage() {
  const navigate = useNavigate();
  const [testing, setTesting] = useState<string | null>(null);
  const [connecting, setConnecting] = useState<string | null>(null);
  const { data, refetch } = useApi<{ integrations: IntegrationSetting[] }>('admin/integrations');

  const integrations = data?.integrations ?? [];

  async function handleConnect(product: string) {
    setConnecting(product);
    try {
      await post(`admin/integrations/${product}/connect`, {});
      refetch();
    } finally {
      setConnecting(null);
    }
  }

  async function handleDisconnect(product: string) {
    if (!confirm(`Disconnect ${PRODUCT_INFO[product as keyof typeof PRODUCT_INFO]?.name ?? product}?`)) return;
    await del(`admin/integrations/${product}/disconnect`);
    refetch();
  }

  async function handleTest(product: string) {
    setTesting(product);
    try {
      await post(`admin/integrations/${product}/test`, {});
      refetch();
    } finally {
      setTesting(null);
    }
  }

  async function handleToggle(integration: IntegrationSetting) {
    await patch(`admin/integrations/${integration.product}`, { enabled: !integration.enabled });
    refetch();
  }

  async function handleFreqChange(product: string, freq: string) {
    await patch(`admin/integrations/${product}`, { sync_frequency: freq });
    refetch();
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/admin')} className="text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h2 className="text-lg font-semibold text-foreground">Integrations</h2>
      </div>

      <div className="space-y-4">
        {(['psa', 'books', 'hub'] as const).map(product => {
          const info = PRODUCT_INFO[product];
          const integration = integrations.find(i => i.product === product);
          const isConnected = integration?.status === 'connected';
          const isEnabled = integration?.enabled ?? false;

          return (
            <Card key={product}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5"><StatusIcon status={integration?.status ?? 'disconnected'} /></div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-foreground">{info.name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${isConnected ? 'bg-green-500/10 text-green-400' : integration?.status === 'error' ? 'bg-red-500/10 text-red-400' : 'bg-surface-3 text-muted-foreground'}`}>
                        {integration?.status ?? 'disconnected'}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{info.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {isConnected && (
                    <>
                      <button
                        onClick={() => handleTest(product)}
                        disabled={testing === product}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-surface-2 hover:bg-surface-3 text-muted-foreground hover:text-foreground text-xs rounded-lg transition-colors disabled:opacity-50"
                      >
                        <RefreshCw className={`w-3 h-3 ${testing === product ? 'animate-spin' : ''}`} /> Test
                      </button>
                      <button
                        onClick={() => handleDisconnect(product)}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-surface-2 hover:bg-red-500/10 text-muted-foreground hover:text-red-400 text-xs rounded-lg transition-colors"
                      >
                        <Unplug className="w-3 h-3" /> Disconnect
                      </button>
                    </>
                  )}
                  {!isConnected && (
                    <button
                      onClick={() => handleConnect(product)}
                      disabled={connecting === product}
                      className="flex items-center gap-1 px-2.5 py-1.5 bg-amber-500 hover:bg-amber-600 text-black text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
                    >
                      <Plug className="w-3 h-3" /> {connecting === product ? 'Connecting...' : 'Connect'}
                    </button>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap gap-1 mb-3">
                {info.features.map(f => (
                  <span key={f} className="text-xs px-2 py-0.5 rounded-full bg-surface-3 text-muted-foreground">{f}</span>
                ))}
              </div>

              {integration && (
                <div className="border-t border-border pt-3 grid md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Sync Direction</p>
                    <p className="text-xs text-foreground">{DIR_LABELS[integration.sync_direction] ?? integration.sync_direction}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Sync Frequency</p>
                    <select
                      value={integration.sync_frequency}
                      onChange={e => handleFreqChange(product, e.target.value)}
                      className="bg-surface-2 border border-border rounded px-2 py-1 text-xs text-foreground"
                    >
                      {Object.entries(FREQ_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Last Sync</p>
                    <p className="text-xs text-foreground">
                      {integration.last_sync_at ? new Date(integration.last_sync_at).toLocaleString() : 'Never'}
                    </p>
                  </div>
                  {integration.error_message && (
                    <div className="md:col-span-3 text-xs text-red-400 bg-red-500/10 rounded px-3 py-2">
                      Error: {integration.error_message}
                    </div>
                  )}
                  <div className="md:col-span-3 flex items-center gap-2">
                    <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isEnabled}
                        onChange={() => handleToggle(integration)}
                        className="rounded"
                      />
                      Integration enabled
                    </label>
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
